import {Clone} from "js-vextensions";
import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, RowLR, Select, Spinner, Text} from "react-vcomponents";
import {TextPlus} from "web-vcore";
import {ResolveMapObjectsForTerrain} from "../../../../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Resolver.js";
import {ShowMaterialExportDialog} from "../../@Shared/AreaExportDialog.js";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ObjPath} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {GetProcMatRecipes, ResolveRecipe} from "../../../../../../Server/DB/mapObjects/ProcMatAccessors.js";
import {MaterialMask, PathMaterialFeature} from "../../../../../../Terrain/Materials/@MaterialFeature.js";
import {collectMaterialRegions, regionContainmentDepth} from "../../../../../../Terrain/Materials/Splat/MaterialRegions.js";
import {ParamSliderRow} from "../../ModifierUIs/TerrainMaterials/RecipeEditor.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";

const splitAt = 120;

export const PathEditorTab_Material = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {obj, path, map, Change} = props;
	const mf = path.materialFeature;

	// The map's recipe catalogue — the same ids the renderer loads (`GetProcMatRecipes`), so the
	// rasteriser's `knownRecipeIDs` never rejects an id chosen here.
	const recipes = GetProcMatRecipes(map.id);

	const allMapObjects = GetMapObjects(map.id);

	// The bridge's per-path world geometry + the RESOLVED region (its derived dominance/nesting
	// depth).  `ResolveMapObjectsForTerrain` is the renderer's own resolution path — the UI's
	// nesting display therefore always matches what the rasteriser actually used.  (Cheap: a
	// handful of paths; the mobx accessor caches it.)
	const terrainObjects = ResolveMapObjectsForTerrain(
		allMapObjects,
		/*resolvePathOriginPathIDs:*/ true,
		/*keepPathMeshLocalPoints:*/ false,
		/*calcPathMeshWorldPoints:*/ true,
	);
	const resolvedRegions = collectMaterialRegions(terrainObjects);
	const regionForThisPath = resolvedRegions.find(r=>r.id == (path.path_id || `${obj.id}_path${(obj.paths ?? []).indexOf(path)}`));
	const nestingDepth = regionForThisPath ? regionContainmentDepth(regionForThisPath, resolvedRegions.filter(r=>r.pathFeature.mode != "stroke")) : 0;

	// Commit a mutated feature back to the path.  `Change` re-runs the parent's full-object
	// clone+diff, so we mutate a clone here and hand it over.
	const ChangeFeature = (mutator: (f: PathMaterialFeature)=>any)=>{
		Change(a=>{
			const next = Clone(a.materialFeature ?? new PathMaterialFeature());
			mutator(next);
			a.materialFeature = next;
		});
	};

	return (
		<Column mt={5}>
			<Row>
				<CheckBox text="Material feature (paints a material region)" value={mf != null && !mf.disabled} onChange={val=>{
					Change(a=>{
						if (val) {
							// First enable: create the struct if it doesn't exist yet (preserving the
							// "if they're trying to enable and the struct doesn't exist yet, THEN add" rule).
							// Subsequent toggles clear `disabled` (set to undefined) rather than adding/removing
							// the struct, so the rest of the config is preserved across disable→re-enable.
							a.materialFeature ??= new PathMaterialFeature();
							a.materialFeature.disabled = undefined;
							// Auto-generate a stable path_id on first enable (drives region ids + RNG seeding).
							if (!a.path_id) a.path_id = ObjPath.NewPathID();
						} else {
							// Disable in-place (keep the struct + its config; consumers IsActive() now returns false).
							if (a.materialFeature) a.materialFeature.disabled = true;
						}
					});
				}}/>
				{mf != null &&
					<Button ml={5} mdIcon="delete" title="Delete material-feature data" onClick={()=>{
						Change(a=>{ a.materialFeature = undefined; });
					}}/>}
			</Row>
			{mf && <>
				{/* Recipe — which catalogue material this path paints. */}
				<Row mt={5}>
					<TextPlus info={`
						Which recipe (catalogue material) this path paints.  The options are the built-ins +
						this map's user recipes (the same set the renderer loads).  Unknown/broken ids are a
						data error at rasterise time (the region is dropped with a warning).
					`.AsMultiline(0)}>Recipe:</TextPlus>
					<Select ml={5} displayType="button bar" options={recipes.map(r=>({name: r.name || r.id, value: r.id}))}
						value={mf.recipeID} onChange={val=>ChangeFeature(f=>f.recipeID = val as string)}/>
				</Row>

				{/* Mode — how the path's geometry becomes a region. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						How the path's geometry becomes a material region (Set12 §3.3 — all three modes are
						ONE signed-distance formula, so this is free at runtime):
						* Fill         -> the interior of a CLOSED path is painted; nothing outside.
						* Stroke       -> terrain within "Stroke width"/2 metres of the polyline is painted
										 (works on open paths too — rivers, roads, ridgelines).
						* Fill + stroke -> the interior PLUS a stroke-width apron spilling outside the ring
										 (an inner fill plus an outer skirt — one region, one SDF).
						Fill / Fill+stroke require the path to be closed (the curve's Closed checkbox above).
						The higher-dominance nested region wins where two overlap; strokes sit above fills by
						default (dominance, below).
					`.AsMultiline(0)}>Mode:</TextPlus>
					<Select displayType="button bar" options={[
						{name: "Fill", value: "fill"},
						{name: "Stroke", value: "stroke"},
						{name: "Fill + stroke", value: "fillAndStroke"},
					]} value={mf.mode} onChange={val=>ChangeFeature(f=>f.mode = val as "fill" | "stroke" | "fillAndStroke")}/>
				</RowLR>
				{(mf.mode == "fill" || mf.mode == "fillAndStroke") && !path.curve_closed && <Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
					<Text>Note: this path is not marked closed — Fill regions are dropped by the terrain system until the curve's "Closed" checkbox is set (inheritance can also flip it).</Text>
				</Row>}

				{/* Stroke width — the FULL stroke thickness. */}
				{(mf.mode == "stroke" || mf.mode == "fillAndStroke") && <RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Full width in metres (the total stroke thickness — extends half this on EACH side of
						the polyline).  Ignored for plain Fill.  A closed path with fillAndStroke spills
						this far OUTSIDE the ring too (the apron).
					`.AsMultiline(0)}>Stroke width (m):</TextPlus>
					<Row>
						<Spinner ml={5} step="any" min={0} value={mf.strokeWidth ?? 0}
							onChange={val=>ChangeFeature(f=>f.strokeWidth = val != null && val > 0 ? val : undefined)}/>
						<Button ml={5} mdIcon="delete" title="Clear (use default)" onClick={()=>ChangeFeature(f=>f.strokeWidth = undefined)}/>
					</Row>
				</RowLR>}

				{/* Blend band. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Blend-band half-width in metres at this region's boundary: how gradually the material
						feathers into what's underneath.  0 = razor-sharp edge (the smoothstep collapses to a
						step at the boundary).  Larger = a softer, more natural transition.  Evaluated per
						fragment in the shader, so any value here is free (no splat re-rasterise, no shader
						recompile — the D1 params-only invariance).
					`.AsMultiline(0)}>Feather (m):</TextPlus>
					<Row>
						<Spinner ml={5} step="any" min={0} value={mf.feather}
							onChange={val=>ChangeFeature(f=>f.feather = val != null && val >= 0 ? val : 0)}/>
						<Button ml={5} mdIcon="delete" title="Reset to default (2)" onClick={()=>ChangeFeature(f=>f.feather = 2)}/>
					</Row>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						The falloff curve inside the feather band.  Smooth = the classic smoothstep (default);
						Linear = a straight ramp; Step = instant (same as feather 0).  Absent = the map's
						blend defaults (the Terrain-materials modifier's "Regions" tab).
					`.AsMultiline(0)}>Feather curve:</TextPlus>
					<Select displayType="button bar" options={[
						{name: "(map default)", value: null},
						{name: "Smooth", value: "smooth"},
						{name: "Linear", value: "linear"},
						{name: "Step", value: "step"},
					]} value={mf.featherCurve ?? null} onChange={val=>ChangeFeature(f=>f.featherCurve = val ?? undefined)}/>
				</RowLR>

				{/* Dominance + nesting depth. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Explicit dominance (higher wins) — overrides the auto-derived value.  Absent (default):
						the region's dominance is derived from containment nesting (§3.4): the number of other
						interior-bearing regions strictly containing this one (so the most-nested ring wins
						automatically), and strokes sit ABOVE all fills (a road crossing a field is on top).
						${
							regionForThisPath
								? `This path's region: nesting depth ${nestingDepth}, resolved dominance ${regionForThisPath.dominance}.`
								: "No resolved region for this path yet (enable + close the path to see it)."
						}
					`.AsMultiline(0)}>Dominance:</TextPlus>
					<Row>
						<Spinner ml={5} step="any" value={mf.dominance}
							onChange={val=>ChangeFeature(f=>f.dominance = val != null ? val : undefined)}/>
						<Button ml={5} mdIcon="delete" title="Clear (use derived nesting depth)" onClick={()=>ChangeFeature(f=>f.dominance = undefined)}/>
					</Row>
				</RowLR>
				{regionForThisPath && mf.dominance == null && <Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
					<Text>Derived dominance: {regionForThisPath.dominance} (nesting depth {nestingDepth}{mf.mode == "stroke" ? "; strokes sit above all fills" : ""})</Text>
				</Row>}

				{/* Opaque. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						When on, this region hard-overrides everything of LOWER dominance (no blend at all —
						its boundary is exactly where it falls off).  The rasteriser clears the strictly-lower
						slots to "absent" inside an opaque winner, so a pixel inside it never shows a trace of
						the under-layer.  Same-dominance regions still blend with each other (the equal-
						dominance soft union).
					`.AsMultiline(0)}>Opaque:</TextPlus>
					<CheckBox value={!!mf.opaque === true} onChange={val=>ChangeFeature(f=>f.opaque = val ? true : undefined)}/>
				</RowLR>

				{/* Soft union (opt-in). */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						When on, this region opts into the SMOOTH soft-union blend when it meets ANOTHER
						region of EQUAL dominance that ALSO has this enabled (a "siblings meet smoothly"
						effect — the two materials share a soft seam + a relative colour blend).
						Default (off): equal-dominance regions use the ordered paint-over — a far-but-bbox-
						overlapping equal-dominance region's distance fades to 0, so it can't bleed in under
						a fading region (the "water replaces base" footgun).  Soft-union only activates when
						EVERY region on both slots has this on; same-recipe regions share a slot, so to
						soft-union just one of a group, fork it into its own recipe.
					`.AsMultiline(0)}>Soft union:</TextPlus>
					<CheckBox value={!!mf.softUnion === true} onChange={val=>ChangeFeature(f=>f.softUnion = val ? true : undefined)}/>
				</RowLR>

				{/* Edge noise. */}
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus info={`
						Boundary noise: warps the region's edge so it reads as natural (gravel/dirt borders
						rather than vector outlines).  Amplitude = metres of displacement; Scale = the noise
						feature size in metres (smaller = finer wiggles).  0 amplitude = a clean vector edge.
						Evaluated per fragment in the shader — free, no re-rasterise, no recompile.
					`.AsMultiline(0)}>Edge noise:</TextPlus>
					<Row>
						<Text>Amp:</Text>
						<Spinner ml={3} step="any" min={0} value={mf.edgeNoise?.amplitude}
							onChange={val=>ChangeFeature(f=>{
								f.edgeNoise ??= {amplitude: 0, scale: 30};
								f.edgeNoise.amplitude = val != null && val > 0 ? val : 0;
								if (f.edgeNoise.amplitude == 0 && (f.edgeNoise.scale ?? 0) == 0) f.edgeNoise = undefined;
							})}/>
						<Text ml={5}>Scale:</Text>
						<Spinner ml={3} step="any" min={0.1} value={mf.edgeNoise?.scale}
							onChange={val=>ChangeFeature(f=>{
								f.edgeNoise ??= {amplitude: 0, scale: 30};
								f.edgeNoise.scale = val != null && val > 0 ? val : 30;
								if (f.edgeNoise.amplitude == 0 && (f.edgeNoise.scale ?? 0) == 0) f.edgeNoise = undefined;
							})}/>
						<Button ml={5} mdIcon="delete" title="Clear (no noise)" enabled={mf.edgeNoise != null}
							onClick={()=>ChangeFeature(f=>f.edgeNoise = undefined)}/>
					</Row>
				</RowLR>

				{/* Param modulations — per-region values for the recipe's regionModulated params. */}
				<ParamModulations_UI recipeID={mf.recipeID} mapID={map.id} value={mf.paramModulations ?? {}} onChange={v=>ChangeFeature(f=>f.paramModulations = v)}/>

				{/* Masks — narrow where this region applies. */}
				<Masks_UI value={mf.masks ?? []} onChange={v=>ChangeFeature(f=>f.masks = v)}/>
			</>}
			<Row mt={8}>
				<Button text="Export area for unit-tests" onClick={()=>{
					ShowMaterialExportDialog(obj, path, allMapObjects, map.id);
				}}/>
			</Row>
		</Column>
	);
});

/** Per-region values for the recipe's `regionModulated` params (the Set9 §6 mechanism).
 *  Reads per REGION (row in params texture) instead of a uniform.  Absent = recipe default. */
const ParamModulations_UI = observer_mgl((props: {
	recipeID: string;
	mapID: string;
	value: {[paramKey: string]: any};
	onChange: (v: {[paramKey: string]: any})=>void;
})=>{
	const {recipeID, mapID, value, onChange} = props;
	let resolved: any = null;
	try { resolved = ResolveRecipe(recipeID, mapID); } catch {}
	const regionModulated = (resolved?.params ?? []).filter((p: any)=>p.regionModulated);
	if (regionModulated.length == 0) return null;
	const setParam = (key: string, v: any)=>{
		const next = {...value};
		if (v === undefined) delete next[key];
		else next[key] = v;
		onChange(next);
	};
	return (
		<>
			<Row mt={5} style={{fontWeight: "bold"}}>Per-region params ({recipeID}):</Row>
			{regionModulated.map((p: any)=>
				<ParamSliderRow key={p.key} param={p} resolvedRecipe={resolved}
					value={value[p.key]}
					onChange={v=>setParam(p.key, v)}/>
			)}
		</>
	);
});

/** `elevationBand` / `slopeBand` filters narrowing where this region applies (Set12 §3.5).
 *  Evaluated per sample on the rasteriser and baked into stored distances, so a mask edit
 *  re-rasterises the splats but never recompiles a shader. */
const Masks_UI = observer_mgl((props: {
	value: MaterialMask[];
	onChange: (v: MaterialMask[])=>void;
})=>{
	const {value, onChange} = props;
	const ChangeMask = (i: number, mutator: (m: MaterialMask)=>any)=>{
		const next = [...value];
		const mask = Clone(next[i]);
		mutator(mask);
		next[i] = mask;
		onChange(next);
	};
	return (
		<>
			<Row mt={5} style={{fontWeight: "bold"}}>Masks:</Row>
			{value.map((mask, i)=>(
				<Column key={i} mt={3} style={{background: "rgba(255,255,255,.08)", padding: 3, borderRadius: 4}}>
					<Row>
						<Select displayType="button bar" options={[
							{name: "Elevation band", value: "elevationBand"},
							{name: "Slope band", value: "slopeBand"},
						]} value={mask.kind} onChange={val=>ChangeMask(i, m=>({...(m as any), kind: val}))}/>
						<Button ml="auto" mdIcon="delete" title="Remove this mask" onClick={()=>onChange(value.filter((_, j)=>j != i))}/>
					</Row>
					<Row mt={3}>
						<Text>Min:</Text>
						<Spinner ml={3} step="any"
							value={mask.min} onChange={val=>ChangeMask(i, m=>m.min = val)}/>
						<Text ml={5}>Max:</Text>
						<Spinner ml={3} step="any"
							value={mask.max} onChange={val=>ChangeMask(i, m=>m.max = val)}/>
						<Text ml={5}>Feather:</Text>
						<Spinner ml={3} step="any" min={0} placeholder={mask.kind == "slopeBand" ? "0.1" : "2"}
							value={mask.feather} onChange={val=>ChangeMask(i, m=>m.feather = val)}/>
						<Button ml={5} mdIcon="delete" title="Clear feather (use the kind-aware default)" onClick={()=>ChangeMask(i, m=>m.feather = undefined)}/>
					</Row>
					<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
						<Text>{mask.kind == "elevationBand"
							? `Only inside elevation ${mask.min}..${mask.max} m (feather default 2 m).`
							: `Only inside slope ${mask.min}..${mask.max} m/m (feather default 0.1 m/m ~6°).`}</Text>
					</Row>
				</Column>
			))}
			<Row mt={3}>
				<Button ml={0} p="1px 8px" text="+ elevation band" title="Narrow this region to an elevation range (e.g. snow above a line)"
					onClick={()=>onChange([...value, {kind: "elevationBand", min: 0, max: 100}])}/>
				<Button ml={5} p="1px 8px" text="+ slope band" title="Narrow this region to a slope range (e.g. bare rock on cliffs)"
					onClick={()=>onChange([...value, {kind: "slopeBand", min: 0.5, max: 2}])}/>
			</Row>
			<Row mt={2} style={{opacity: 0.6, fontSize: 11}}>
				<Text>Masks multiply the region's weight: a sample outside every mask is treated as outside the region (a masked high-dominance region does NOT outrank an unmasked lower one).</Text>
			</Row>
		</>
	);
});